import { t, Trans } from '@lingui/macro';
import React, { useEffect, useMemo, useState } from 'react';

import { keyPressHelper } from '@brandfolder/utilities';
import { CommonFaceResponse, getCommonFaces, splitIdentity } from '@api/v4/private/people_tagging/faces';
import { Locale } from '@components/common/language_menu/languagesMap';
import { BulkCustomFieldsList } from '@components/show_page/bulk_actions/sub_components/BulkCustomFieldsList';
import BulkPeopleContent from '@components/show_page/bulk_actions/sub_components/BulkPeopleContent';
import TagsModalContent from '@components/show_page/bulk_actions/sub_components/TagsModalContent';
import { unsavedChangesOptions } from '@helpers/sweet_alert_options';

import renderModal from '../modals/renderModal';

enum TabType {
  Tags = 'tags',
  People = 'people',
  CustomFields = 'customFields',
}

interface BulkTagWrapperProps {
  closeModal: () => void;
  selectedAssetKeys: Set<string>;
  ugtLocale?: Locale;
}

const BulkTagWrapper: React.FC<BulkTagWrapperProps> = ({
  closeModal,
  selectedAssetKeys,
  ugtLocale,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(TabType.Tags);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removedFaceIds, setRemovedFaceIds] = useState<Set<string>>(new Set());
  const [facesToSplit, setFacesToSplit] = useState<string[][]>([]);
  const [isLoadingFaces, setIsLoadingFaces] = useState(false);
  const [faces, setFaces] = useState<CommonFaceResponse[]>([]);
  const [hasFetchedFaces, setHasFetchedFaces] = useState(false);

  useEffect(() => {
    // Track Bulk Tag modal page load with Amplitude
    if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
      window.amplitudeService.trackEvent('PageLoad', {
        elementName: 'BulkTagView',
        elementLocation: 'BulkTagModal',
        productArea: 'BulkActionBar',
        featureArea: 'BulkTagModal',
        eventName: 'PageLoad',
        'key properties': {
          assetIds: Array.from(selectedAssetKeys || [])
        }
      });
    }

    return () => { closeModal(); };
  }, [closeModal, selectedAssetKeys]);

  // Fetch faces when People tab is activated
  useEffect(() => {
    if (activeTab === TabType.People && !hasFetchedFaces) {
      setIsLoadingFaces(true);
      getCommonFaces({
        brandfolderKey: BFG.resource.key,
        assetKeys: Array.from(selectedAssetKeys),
      })
        .then((data) => {
          setFaces(data);
          setHasFetchedFaces(true);
        })
        .catch((error) => {
          console.error('Error fetching faces:', error);
          Notify.create({
            title: t`Failed to load faces. Please try again.`,
            type: 'error',
          });
        })
        .finally(() => {
          setIsLoadingFaces(false);
        });
    }
  }, [activeTab, hasFetchedFaces, selectedAssetKeys]);

  const toggleTab = (tab: TabType): void => {
    if (hasUnsavedChanges) {
      window.swal(unsavedChangesOptions({
        onLeaveCopy: t`Discard Changes`,
        onStayCopy: t`Continue Editing`,
      }), (confirm: boolean) => {
        if (confirm) {
          setActiveTab(tab);
          setHasUnsavedChanges(false);
          setRemovedFaceIds(new Set());
          setFacesToSplit([]);
        }
      });
    } else {
      setActiveTab(tab);
      setHasUnsavedChanges(false);
    }
  };

  const handleClose = (): void => {
    if (hasUnsavedChanges) {
      window.swal(unsavedChangesOptions({
        onStayCopy: t`Continue Editing`,
      }), (confirm: boolean) => {
        if (confirm) {
          closeModal();
        }
      });
    } else {
      closeModal();
    }
  };

  const handleRemoveFace = (faceIds: string[]): void => {
    setRemovedFaceIds((prev) => {
      const updated = new Set(prev);
      faceIds.forEach((id) => updated.add(id));
      return updated;
    });
    setFacesToSplit((prev) => {
      // Check if this face group is already in the list
      const faceIdsStr = faceIds.sort().join(',');
      const alreadyExists = prev.some(
        (existingFaceIds) => existingFaceIds.sort().join(',') === faceIdsStr
      );
      if (alreadyExists) {
        return prev;
      }
      return [...prev, faceIds];
    });
    setHasUnsavedChanges(true);
  };

  const handleSave = async (): Promise<void> => {
    setIsSubmitting(true);

    try {
      // Make a separate API call for each face group to split (remove identity from)
      const promises = facesToSplit.map((faceIds) =>
        splitIdentity({
          brandfolderKey: BFG.resource.key,
          faceIds,
        })
      );

      await Promise.all(promises);

      Notify.create({
        title: t`Processing people tag updates!`,
        body: t`Updates will be available shortly.`,
        type: 'success',
      });
      // Remove the deleted faces from state so they don't reappear
      setFaces((prevFaces) => prevFaces.filter(
        (face) => !face.face_ids.some((id) => removedFaceIds.has(id))
      ));
      setHasUnsavedChanges(false);
      setRemovedFaceIds(new Set());
      setFacesToSplit([]);
    } catch (error) {
      console.error('Error removing people tags:', error);
      Notify.create({
        title: t`Failed to remove people tags. Please try again.`,
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasCustomFields = BFG.context.hasFeature('custom_fields');
  const assetCount = selectedAssetKeys.size;

  // Memoize visible faces to avoid recalculating on every render
  const visibleFaces = useMemo(() => (
    faces.filter((face) => !face.face_ids.some((id) => removedFaceIds.has(id)))
  ), [faces, removedFaceIds]);

  const handlePeopleTabClick = () => toggleTab(TabType.People);
  const handleTagsTabClick = () => toggleTab(TabType.Tags);
  const handleCustomFieldsTabClick = () => {
    // Track Bulk Custom Fields tab select with Amplitude
    if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
      window.amplitudeService.trackEvent('TabSelect', {
        elementName: 'BulkCustomFieldsTab',
        elementLocation: 'BulkTagModal',
        productArea: 'BulkActionBar',
        featureArea: 'BulkTagModal',
        eventName: 'TabSelect',
        'key properties': {
          assetIds: Array.from(selectedAssetKeys || [])
        }
      });
    }
    toggleTab(TabType.CustomFields);
  };

  return (
    <div className="bulk-tag-wrapper">
      <div className="modal-content-wrapper__header wrapper-headers" role="tablist">
        {BFG.context.hasFeature('people_tagging') && BFG.brandfolderSettings.people_tagging && (
          <div
            className={`header ${activeTab === TabType.People ? 'active' : ''}`}
            onClick={handlePeopleTabClick}
            onKeyDown={keyPressHelper(handlePeopleTabClick)}
            role="tab"
            tabIndex={0}
            aria-selected={activeTab === TabType.People}
            aria-controls="bulk-tag-people-panel"
            id="bulk-tag-people-tab"
          >
            <span aria-hidden="true" className="bff-people icon" />
            <h3 className="modal-title"><Trans>People</Trans></h3>
          </div>
        )}
        <div
          className={`header ${activeTab === TabType.Tags ? 'active' : ''} ${!hasCustomFields ? 'one-tab' : ''}`}
          onClick={handleTagsTabClick}
          onKeyDown={keyPressHelper(handleTagsTabClick)}
          role="tab"
          tabIndex={0}
          aria-selected={activeTab === TabType.Tags}
          aria-controls="bulk-tag-tags-panel"
          id="bulk-tag-tags-tab"
        >
          <span aria-hidden="true" className="bff-tag icon" />
          <h3 className="modal-title"><Trans>Bulk Tag</Trans></h3>
        </div>
        {hasCustomFields && (
          <div
            className={`header ${activeTab === TabType.CustomFields ? 'active' : ''}`}
            onClick={handleCustomFieldsTabClick}
            onKeyDown={keyPressHelper(handleCustomFieldsTabClick)}
            role="tab"
            tabIndex={0}
            aria-selected={activeTab === TabType.CustomFields}
            aria-controls="bulk-tag-custom-fields-panel"
            id="bulk-tag-custom-fields-tab"
          >
            <span aria-hidden="true" className="bff-custom-fields icon" />
            <h3 className="modal-title"><Trans>Bulk Custom Fields</Trans></h3>
          </div>
        )}
      </div>
      {activeTab === TabType.People && BFG.context.hasFeature('people_tagging') ? (
        <div role="tabpanel" id="bulk-tag-people-panel" aria-labelledby="bulk-tag-people-tab">
          <BulkPeopleContent
            assetCount={assetCount}
            handleClose={handleClose}
            handleRemoveFace={handleRemoveFace}
            handleSave={handleSave}
            isLoadingFaces={isLoadingFaces}
            isSubmitting={isSubmitting}
            removedFaceIdsCount={removedFaceIds.size}
            visibleFaces={visibleFaces}
          />
        </div>
      ) : activeTab === TabType.CustomFields ? (
        <div role="tabpanel" id="bulk-tag-custom-fields-panel" aria-labelledby="bulk-tag-custom-fields-tab">
          <BulkCustomFieldsList
            closeModal={closeModal}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedAssetKeys={selectedAssetKeys}
            setHasUnsavedChanges={setHasUnsavedChanges}
            ugtLocale={ugtLocale as Locale | undefined}
          />
        </div>
      ) : (
        <div role="tabpanel" id="bulk-tag-tags-panel" aria-labelledby="bulk-tag-tags-tab">
          <TagsModalContent
            closeModal={closeModal}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedAssetKeys={selectedAssetKeys}
            setHasUnsavedChanges={setHasUnsavedChanges}
            ugtLocale={ugtLocale}
          />
        </div>
      )}
    </div>
  );
};

const ModalComponent = renderModal(BulkTagWrapper, 'BulkTagWrapper');
export default ModalComponent;
